'use client';

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AnalysisProgress as Progress } from '../hooks/useInterferenceAnalysis';

const phaseLabels = {
  idle: 'Preparing analysis',
  workload: 'Loading workload metrics',
  ratelimit: 'Loading rate-limit metrics',
  analyzing: 'Running rare-event analysis',
  complete: 'Analysis complete',
  cancelled: 'Analysis cancelled',
  error: 'Loading stopped',
};

function formatDuration(milliseconds: number): string {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
}

export function AnalysisProgress({ progress }: { progress: Progress }) {
  const [now, setNow] = useState(0);

  useEffect(() => {
    if (progress.phase !== 'analyzing') return;
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, [progress.phase, progress.analysisStartedAtMs]);

  const elapsed = progress.analysisStartedAtMs ? Math.max(0, now - progress.analysisStartedAtMs) : 0;

  return (
    <div role="status" className="rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-center gap-2 text-sm font-medium">
        {progress.phase !== 'complete' && progress.phase !== 'error' && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        {phaseLabels[progress.phase]}
      </div>
      {(progress.workloadPages > 0 || progress.ratelimitPages > 0) && <div className="mt-3 grid gap-2 text-xs text-muted-foreground sm:grid-cols-2">
        <div className="rounded bg-muted/50 px-3 py-2">
          Workload: {progress.workloadPages} page{progress.workloadPages === 1 ? '' : 's'} · {progress.workloadIntervals.toLocaleString()} intervals
        </div>
        <div className="rounded bg-muted/50 px-3 py-2">
          Rate limit: {progress.ratelimitPages} page{progress.ratelimitPages === 1 ? '' : 's'} · {progress.ratelimitEntries.toLocaleString()} entries
        </div>
      </div>}
      {progress.phase === 'analyzing' && (
        <div className="mt-3">
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="font-medium text-foreground">Fetching metrics and running analysis</span>
            <span className="text-muted-foreground">{formatDuration(elapsed)} elapsed</span>
          </div>
          <div
            role="progressbar"
            aria-label="Analysis in progress"
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-2 overflow-hidden rounded-full bg-muted"
          >
            <div className="h-full w-2/3 animate-pulse rounded-full bg-primary" />
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">Metrics are fetched and processed by the backend; no large dataset is sent through the browser.</p>
        </div>
      )}
    </div>
  );
}
